import React from "react";
import { useParams } from "react-router-dom";
import styled, { css } from "styled-components";

import { parseObjectivesView } from "@examspark/api";
import { Breadcrumbs, useQueryGetObjectivesView } from "@examspark/common-app";
import { FlexBox } from "@examspark/common-ui";

import PageHead from "../../components/common/PageHead";
import PageTitle from "../../components/common/PageTitle";
import ClarificationDocument from "../../components/objectives/ClarificationDocument";
import ConceptualAlignment from "../../components/objectives/ConceptualAlignment";
import VerticalAlignment from "../../components/objectives/VerticalAlignment";
import { AsideLayoutProvider } from "../../contexts/AsideLayoutContext";

const ClassroomAside = styled.aside`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999;
`;

const Layout = styled.div<{ requestAside: boolean }>`
  padding-bottom: 75dvh;

  // if we have enough room, we scoot the contet over
  // when an aside is requested
  @media (min-width: 1024px) {
    ${({ requestAside }) =>
      requestAside &&
      css`
        display: grid;
        grid-template-columns: 1fr calc(48rem - max(50dvw - 50%, 0px));
        gap: 2rem;
      `}
  }

  // otherwise, we just float the aside fixed at the bottom of the screen
`;

const ObjectivesViewRoute = () => {
  const params = useParams<{
    id: string;
  }>();
  const { data } = useQueryGetObjectivesView(params.id);

  const [requestAside, setRequestAside] = React.useState<
    Record<string, boolean>
  >({});

  const parsed = parseObjectivesView(data);

  const renderObjectivesView = () => {
    switch (parsed?.type) {
      case "CLARIFICATION":
        return <ClarificationDocument data={parsed} />;
      case "CONCEPTUAL_ALIGNMENT":
        return <ConceptualAlignment data={parsed} />;
      case "VERTICAL_ALIGNMENT":
        return <VerticalAlignment data={parsed} />;
      default:
        return null;
    }
  };

  return (
    <AsideLayoutProvider
      requestAside={requestAside}
      onRequestAside={(id, request) => {
        setRequestAside((prev) => ({ ...prev, [id]: request }));
      }}
      asidePortalId="classroom-aside"
      breakAt={"1200px"}
    >
      <Layout requestAside={Object.values(requestAside).some(Boolean)}>
        <FlexBox style={{ gap: "1rem" }}>
          <PageHead>
            <Breadcrumbs items={Breadcrumbs.Browse()} />
            <PageTitle>
              {data?.name ? data?.name : "Loading Document..."}
            </PageTitle>
          </PageHead>
          {renderObjectivesView()}
        </FlexBox>
      </Layout>
      <ClassroomAside id="classroom-aside" />
    </AsideLayoutProvider>
  );
};

export default ObjectivesViewRoute;
